import { ReactNode, isValidElement } from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Loader from './Loader';
import { SxProps, Theme } from '@mui/material/styles';

type StandardTableProps = {
  id: string;
  headers: ReactNode[];
  rows: ReactNode[][];
  className?: string;
  loading?: boolean;
  noDataMessage?: string;
  noDataColor?: 'error' | 'primary' | 'secondary' | 'default';
  wrapperProps?: {
    sx?: SxProps<Theme>;
    [key: string]: any;
  };
  tableProps?: {
    stickyHeader?: boolean;
    sx?: SxProps<Theme>;
    [key: string]: any;
  };
  extraScrollWidth?: string;
  scroll?: boolean;
  headerSx?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
  sx?: Record<string, any>;
};

export default function StandardTable({
  id,
  headers,
  rows,
  loading = false,
  noDataMessage = 'No results found',
  noDataColor = 'error',
  wrapperProps = {},
  tableProps = {},
  extraScrollWidth = '',
  scroll = false,
  headerSx = {},
  bodySx = {},
  sx = {},
}: StandardTableProps) {
  const {
    className: wrapperClassName = '',
    sx: wrapperSx = {},
    ...otherWrapperProps
  } = wrapperProps;
  const { stickyHeader } = tableProps;

  return (
    <Box sx={{ overflow: 'auto' }}>
      <TableContainer
        sx={{
          width: extraScrollWidth || '100%',
          overflow: stickyHeader || scroll || extraScrollWidth ? 'auto' : 'hidden',
          ...wrapperSx,
        }}
        {...otherWrapperProps}
      >
        <Table aria-label={`${id}-table`} sx={sx} {...tableProps}>
          <TableHead id={`${id}-tablehead`}>
            <TableRow sx={headerSx}>
              {headers.map((headerCell, index) => {
                if (isValidElement(headerCell) && typeof headerCell.props === 'object') {
                  const { children, ...props } = headerCell.props as {
                    children?: ReactNode;
                    [key: string]: unknown;
                  };
                  return (
                    <TableCell
                      key={`${id}-header-${index}`}
                      sx={{ fontWeight: 600, ...headerSx }}
                      {...props}
                    >
                      {children ?? headerCell}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={`${id}-header-${index}`} sx={{ fontWeight: 600, ...headerSx }}>
                    {headerCell}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {!loading && (
            <TableBody id={`${id}-tablebody`}>
              {rows.map((row, rowIndex) => (
                <TableRow key={`${id}-row-${rowIndex}`} sx={bodySx}>
                  {row.map((rowCell, cellIndex) => {
                    if (isValidElement(rowCell) && typeof rowCell.props === 'object') {
                      const { children, ...props } = rowCell.props as {
                        children?: ReactNode;
                        [key: string]: unknown;
                      };
                      return (
                        <TableCell
                          key={`${id}-row-${rowIndex}-${cellIndex}`}
                          sx={bodySx}
                          {...props}
                        >
                          {children ?? rowCell}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={`${id}-row-${rowIndex}-${cellIndex}`} sx={bodySx}>
                        {rowCell}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {rows.length === 0 && !loading && (
          <Box mt={1} sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
            <Typography variant="subtitle1" color={noDataColor}>
              {noDataMessage}
            </Typography>
          </Box>
        )}
        {loading && (
          <Box mt={1} sx={{ display: 'grid', placeItems: 'center' }}>
            <Loader size="sm" />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
}

type HeaderData = string | ReactNode;
type AdjustableColumn = {
  colName: string;
  value: string | number;
};

export function makeHeader(
  id: string,
  headerData: HeaderData[],
  adjustableColumns: AdjustableColumn[] = []
): ReactNode[] {
  return headerData.map((headerDatum, index) => {
    const entry = adjustableColumns.find((e) => e.colName === headerDatum);
    return (
      <TableCell
        key={`${id}-${index}-cell`}
        variant="head"
        width={entry ? entry.value : undefined}
        sx={{ borderBottom: 'none' }}
      >
        {headerDatum}
      </TableCell>
    );
  });
}

type RowData = string | number | ReactNode | { content: ReactNode; [key: string]: any };

export function makeRow(id: string, rowData: RowData[], isLit = false): ReactNode[] {
  return rowData.map((rowDatum, index) => {
    const borderLeft = isLit && index === 0 ? '6px solid #0361A0' : '0px';
    const paddingLeft = isLit && index === 0 ? '13px' : undefined;
    const cellProps = { key: `${id}-${index}`, style: { borderLeft, paddingLeft } };

    if (rowDatum === '' || rowDatum === null || rowDatum === undefined) {
      return <TableCell {...cellProps}>&nbsp;</TableCell>;
    }

    if (rowDatum && typeof rowDatum === 'object' && !isValidElement(rowDatum)) {
      const { content, ...options } = rowDatum as { content: ReactNode; [key: string]: any };
      if (content === '' || content === null || content === undefined) {
        return (
          <TableCell {...cellProps} {...options}>
            &nbsp;
          </TableCell>
        );
      }
      return (
        <TableCell {...cellProps} {...options}>
          {content}
        </TableCell>
      );
    }
    return <TableCell {...cellProps}>{rowDatum}</TableCell>;
  });
}
